import { useInfiniteQuery } from "@tanstack/react-query";
import { fetchClient } from "@/lib/api-client";

interface UseFetchFilteredOrdersParams {
  wallet?: string;
  bvSide?: "left" | "right" | "both";
  minBv?: number;
  maxBv?: number;
  weekNumber?: number;
  date?: string; // ISO format YYYY-MM-DD
  startDate?: string; // ISO format YYYY-MM-DD
  endDate?: string; // ISO format YYYY-MM-DD
  limit?: number;
  enabled?: boolean;
}

export const useFetchFilteredOrders = (
  params?: UseFetchFilteredOrdersParams,
) => {
  const {
    wallet,
    bvSide,
    minBv,
    maxBv,
    weekNumber,
    date,
    startDate,
    endDate,
    limit = 10,
    enabled = true,
  } = params || {};

  const queryFn = async ({ pageParam = 1 }) => {
    const response = await fetchClient.GET("/nft/filtered-orders", {
      params: {
        query: {
          page: typeof pageParam === "number" ? pageParam : 1,
          limit,
          ...(wallet && { wallet }),
          ...(bvSide && { bvSide }),
          ...(minBv !== undefined && { minBv }),
          ...(maxBv !== undefined && { maxBv }),
          ...(weekNumber !== undefined && { weekNumber }),
          ...(date && { date }),
          ...(startDate && { startDate }),
          ...(endDate && { endDate }),
        },
      },
    });
    const error = response.error;
    if (error) throw error;
    return response.data;
  };

  const {
    data,
    error,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetching,
    isFetchNextPageError,
  } = useInfiniteQuery({
    queryKey: [
      "user",
      "filtered-orders",
      wallet,
      bvSide,
      minBv,
      maxBv,
      weekNumber,
      date,
      startDate,
      endDate,
      limit,
    ],
    queryFn,
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      const pagination = lastPage?.data?.pagination;

      if (pagination?.hasNextPage) return pagination.page + 1;

      return undefined;
    },
    enabled,
    refetchOnReconnect: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchInterval: false,
    refetchIntervalInBackground: false,
  });

  return {
    data,
    error,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetching,
    isFetchNextPageError,
  };
};
